import { useCallback, useMemo, useReducer } from "react";

/**
 * A line on the ticket.
 *
 * `name` and `priceCents` are copied off the menu item when it is added rather
 * than looked up later, so the ticket keeps reading correctly even if the menu
 * is refetched underneath it. The API takes the same view — an order stores
 * `nameSnapshot` and `priceCentsSnapshot`.
 */
export interface CartLine {
  readonly menuItemId: string;
  readonly name: string;
  /** Integer cents. Never divided until it is rendered. */
  readonly priceCents: number;
  readonly quantity: number;
}

/** What the menu hands over when something is added. */
export type CartAddition = Omit<CartLine, "quantity">;

type CartAction =
  | { type: "add"; item: CartAddition }
  | { type: "decrement"; menuItemId: string }
  | { type: "remove"; menuItemId: string }
  | { type: "clear" };

/** Lines stay in the order they were first added, which is how a ticket prints. */
function reduce(lines: readonly CartLine[], action: CartAction): readonly CartLine[] {
  switch (action.type) {
    case "add": {
      const existing = lines.find((l) => l.menuItemId === action.item.menuItemId);
      if (!existing) return [...lines, { ...action.item, quantity: 1 }];
      return lines.map((l) =>
        l.menuItemId === action.item.menuItemId ? { ...l, quantity: l.quantity + 1 } : l,
      );
    }
    case "decrement": {
      // Dropping to zero removes the line rather than leaving a 0× row on the
      // ticket, which is what the stepper's "−" reaching 1 should look like.
      return lines.flatMap((l) => {
        if (l.menuItemId !== action.menuItemId) return [l];
        return l.quantity <= 1 ? [] : [{ ...l, quantity: l.quantity - 1 }];
      });
    }
    case "remove":
      return lines.filter((l) => l.menuItemId !== action.menuItemId);
    case "clear":
      return [];
  }
}

const EMPTY: readonly CartLine[] = [];

export interface Cart {
  readonly lines: readonly CartLine[];
  /** Integer cents. */
  readonly totalCents: number;
  /** Total number of things, not number of lines. */
  readonly count: number;
  /** Quantity of one menu item, 0 when it is not on the ticket. */
  quantityOf: (menuItemId: string) => number;
  add: (item: CartAddition) => void;
  decrement: (menuItemId: string) => void;
  remove: (menuItemId: string) => void;
  clear: () => void;
}

export function useCart(): Cart {
  const [lines, dispatch] = useReducer(reduce, EMPTY);

  // Derived, never stored: a total kept in state alongside the lines is a
  // second source of truth that can disagree with them.
  const totalCents = useMemo(
    () => lines.reduce((sum, l) => sum + l.priceCents * l.quantity, 0),
    [lines],
  );
  const count = useMemo(() => lines.reduce((n, l) => n + l.quantity, 0), [lines]);

  const quantities = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of lines) map.set(l.menuItemId, l.quantity);
    return map;
  }, [lines]);

  const quantityOf = useCallback(
    (menuItemId: string): number => quantities.get(menuItemId) ?? 0,
    [quantities],
  );

  const add = useCallback((item: CartAddition) => dispatch({ type: "add", item }), []);
  const decrement = useCallback(
    (menuItemId: string) => dispatch({ type: "decrement", menuItemId }),
    [],
  );
  const remove = useCallback(
    (menuItemId: string) => dispatch({ type: "remove", menuItemId }),
    [],
  );
  const clear = useCallback(() => dispatch({ type: "clear" }), []);

  return { lines, totalCents, count, quantityOf, add, decrement, remove, clear };
}
